import type { Timestamp } from 'firebase/firestore'

export type DesignMode = 'BOUND' | 'FREE'

export const DESIGN_CANVAS_GRID = 8
export const DESIGN_DEFAULT_VIEWPORT = { zoom: 1, x: 0, y: 0 }

export type DesignShapeType =
  | 'RECT'
  | 'ROUNDED_RECT'
  | 'ELLIPSE'
  | 'TRIANGLE'
  | 'POLYGON'
  | 'STAR'
  | 'LINE'
  | 'ARROW'
  | 'POLYLINE'
  | 'CURVE'
  | 'DIAMOND'
  | 'PARALLELOGRAM'
  | 'HEXAGON'
  | 'CYLINDER'
  | 'DOCUMENT'
  | 'TERMINATOR'
  | 'OFF_PAGE'
  | 'PROCESS'
  | 'BUBBLE'
  | 'TEXT'
  | 'STICKY'
  | 'FREEHAND'
  | 'IMAGE'
  | 'ICON'
  | 'CHART'
  | 'CONNECTOR'
  | 'GROUP'

export const FLOWCHART_SHAPE_TYPES: DesignShapeType[] = [
  'RECT',
  'ROUNDED_RECT',
  'ELLIPSE',
  'DIAMOND',
  'PARALLELOGRAM',
  'HEXAGON',
  'CYLINDER',
  'DOCUMENT',
  'TERMINATOR',
  'OFF_PAGE',
  'PROCESS',
  'BUBBLE',
]

export const BOUNDABLE_SHAPE_TYPES: DesignShapeType[] = [
  'RECT',
  'ROUNDED_RECT',
  'ELLIPSE',
  'DIAMOND',
  'PARALLELOGRAM',
  'HEXAGON',
  'CYLINDER',
  'DOCUMENT',
  'TERMINATOR',
  'OFF_PAGE',
  'PROCESS',
  'BUBBLE',
  'TEXT',
  'CHART',
  'CONNECTOR',
]

export type DesignColor = string

export type DesignGradientStop = {
  offset: number
  color: DesignColor
}

export type DesignGradient = {
  kind: 'LINEAR' | 'RADIAL'
  angle: number
  stops: DesignGradientStop[]
}

export type DesignFill =
  | { kind: 'SOLID'; color: DesignColor }
  | { kind: 'GRADIENT'; gradient: DesignGradient }
  | { kind: 'NONE' }

export type DesignStrokeStyle = 'SOLID' | 'DASHED' | 'DOTTED'

export type DesignStroke = {
  color: DesignColor
  width: number
  style: DesignStrokeStyle
}

export type DesignShadow = {
  kind: 'DROP' | 'INNER'
  x: number
  y: number
  blur: number
  spread: number
  color: DesignColor
}

export type DesignBlur = {
  kind: 'LAYER' | 'BACKGROUND'
  radius: number
}

export type DesignEffect =
  | ({ id: string; effect: 'SHADOW' } & DesignShadow)
  | ({ id: string; effect: 'BLUR' } & DesignBlur)

export type DesignBlendMode =
  | 'NORMAL'
  | 'MULTIPLY'
  | 'SCREEN'
  | 'OVERLAY'
  | 'DARKEN'
  | 'LIGHTEN'
  | 'COLOR_DODGE'
  | 'COLOR_BURN'
  | 'HARD_LIGHT'
  | 'SOFT_LIGHT'
  | 'DIFFERENCE'
  | 'EXCLUSION'
  | 'HUE'
  | 'SATURATION'
  | 'COLOR'
  | 'LUMINOSITY'

export type DesignTextStyle = {
  fontFamily: string
  fontSize: number
  fontWeight: number
  align: 'LEFT' | 'CENTER' | 'RIGHT'
  color: DesignColor
  lineHeight: number
  letterSpacing: number
}

export const DEFAULT_TEXT_STYLE: DesignTextStyle = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 16,
  fontWeight: 500,
  align: 'CENTER',
  color: '#f8fafc', // mosaic-keep: data (default text style color)
  lineHeight: 1.35,
  letterSpacing: 0,
}

export type DesignFreehandPoint = { x: number; y: number; pressure?: number }

export type DesignConnectorRouting = 'STRAIGHT' | 'ORTHOGONAL' | 'CURVED'
export type DesignConnectorArrow = 'NONE' | 'TRIANGLE' | 'OPEN' | 'DIAMOND' | 'CIRCLE'

export type DesignConnectorEndpoint =
  | { kind: 'SHAPE'; shapeId: string; port?: 'N' | 'S' | 'E' | 'W' | 'AUTO' }
  | { kind: 'FREE'; x: number; y: number }

export type DesignLogicTrigger = {
  condition: 'PROGRESS_GTE' | 'PROGRESS_EQ' | 'STATUS_EQ'
  value: string | number
}

export type DesignBinding = {
  linkedNodeId: string | null
  fields?: Array<'title' | 'color' | 'progress'>
}

export type BoundSemanticRole =
  | 'CARD'
  | 'CHECKLIST_NODE'
  | 'GATE_AND'
  | 'GATE_OR'
  | 'FORM_FIELD'
  | 'FORM_SECTION'
  | 'AUTOMATION_TRIGGER'
  | 'AUTOMATION_ACTION'
  | 'AUTOMATION_BRANCH'
  | 'KPI'

export const BOUND_SEMANTIC_ROLES: BoundSemanticRole[] = [
  'CARD',
  'CHECKLIST_NODE',
  'GATE_AND',
  'GATE_OR',
  'FORM_FIELD',
  'FORM_SECTION',
  'AUTOMATION_TRIGGER',
  'AUTOMATION_ACTION',
  'AUTOMATION_BRANCH',
  'KPI',
]

export type DesignShapeBase = {
  id: string
  name?: string
  position: { x: number; y: number }
  size: { width: number; height: number }
  rotation: number
  opacity: number
  fill: DesignFill
  stroke: DesignStroke | null
  effects: DesignEffect[]
  blendMode: DesignBlendMode
  locked?: boolean
  hidden?: boolean
  groupId?: string | null
  binding?: DesignBinding
  logicTrigger?: DesignLogicTrigger | null
  text?: string
  textStyle?: DesignTextStyle
  /** Additive Bound metadata — Phase A of the Bound engine plan. Optional; ignored by legacy readers. */
  semanticRole?: BoundSemanticRole
}

export type DesignRectShape = DesignShapeBase & {
  type: 'RECT' | 'ROUNDED_RECT' | 'PROCESS'
  cornerRadius?: number
}

export type DesignEllipseShape = DesignShapeBase & {
  type: 'ELLIPSE' | 'BUBBLE'
}

export type DesignPolyShape = DesignShapeBase & {
  type: 'TRIANGLE' | 'DIAMOND' | 'PARALLELOGRAM' | 'HEXAGON' | 'CYLINDER' | 'DOCUMENT' | 'TERMINATOR' | 'OFF_PAGE'
}

export type DesignPolygonShape = DesignShapeBase & {
  type: 'POLYGON' | 'STAR'
  sides: number
  innerRatio?: number
}

/**
 * Free-Studio drawing overhaul (PLAN 4). Lines now carry an optional `path`
 * union so straight lines can be stored point-to-point (any angle), rather
 * than reconstructed from the AABB (which forced them horizontal).
 * Legacy assets have no `path` — the renderer keeps the AABB fallback.
 */
export type DesignLinePath =
  | { variant: 'STRAIGHT'; p1: { x: number; y: number }; p2: { x: number; y: number } }

export type DesignLineShape = DesignShapeBase & {
  type: 'LINE' | 'ARROW'
  arrowStart?: DesignConnectorArrow
  arrowEnd?: DesignConnectorArrow
  path?: DesignLinePath
}

/** Multi-segment polyline. Click-click-double-click authoring UX. */
export type DesignPolylineShape = DesignShapeBase & {
  type: 'POLYLINE'
  points: Array<{ x: number; y: number }>
  arrowStart?: DesignConnectorArrow
  arrowEnd?: DesignConnectorArrow
}

/** Cubic Bezier: end points p1/p2 with two control points c1/c2 for smooth curves. */
export type DesignCurveShape = DesignShapeBase & {
  type: 'CURVE'
  p1: { x: number; y: number }
  p2: { x: number; y: number }
  c1: { x: number; y: number }
  c2: { x: number; y: number }
  arrowStart?: DesignConnectorArrow
  arrowEnd?: DesignConnectorArrow
}

export type DesignTextShape = DesignShapeBase & {
  type: 'TEXT'
}

export type DesignStickyShape = DesignShapeBase & {
  type: 'STICKY'
}

export type DesignFreehandShape = DesignShapeBase & {
  type: 'FREEHAND'
  points: DesignFreehandPoint[]
  smoothing?: number
}

export type DesignImageShape = DesignShapeBase & {
  type: 'IMAGE'
  src: string
  storagePath?: string
  naturalWidth?: number
  naturalHeight?: number
  crop?: { x: number; y: number; width: number; height: number }
}

export type DesignIconShape = DesignShapeBase & {
  type: 'ICON'
  iconName: string
  iconColor: DesignColor
}

export type DesignChartShape = DesignShapeBase & {
  type: 'CHART'
  chartId: string
}

export type DesignConnectorShape = DesignShapeBase & {
  type: 'CONNECTOR'
  from: DesignConnectorEndpoint
  to: DesignConnectorEndpoint
  routing: DesignConnectorRouting
  arrowStart: DesignConnectorArrow
  arrowEnd: DesignConnectorArrow
}

export type DesignGroupShape = DesignShapeBase & {
  type: 'GROUP'
  childIds: string[]
}

export type DesignShape =
  | DesignRectShape
  | DesignEllipseShape
  | DesignPolyShape
  | DesignPolygonShape
  | DesignLineShape
  | DesignPolylineShape
  | DesignCurveShape
  | DesignTextShape
  | DesignStickyShape
  | DesignFreehandShape
  | DesignImageShape
  | DesignIconShape
  | DesignChartShape
  | DesignConnectorShape
  | DesignGroupShape

/** Shape types that render as strokes-in-space (no meaningful fill). Used by
 * hit-testing (stroke-only, no AABB grab-zone) and the property panel (no
 * width/height/rotation; endpoint-drag or Redraw instead). */
export const STROKE_SHAPE_TYPES: DesignShapeType[] = ['LINE', 'ARROW', 'POLYLINE', 'CURVE', 'FREEHAND', 'CONNECTOR']
export const isStrokeShape = (shape: DesignShape): boolean =>
  (STROKE_SHAPE_TYPES as string[]).includes(shape.type)

export type BoundIntent =
  | 'DASHBOARD'
  | 'CHECKLIST_BLUEPRINT'
  | 'FORM_LAYOUT'
  | 'AUTOMATION_MAP'
  | 'MIXED'

export type BoundOutputStatus = 'NONE' | 'DRAFT' | 'MATERIALIZED' | 'STALE'

export type BoundOutput = {
  kind: BoundIntent
  /** id of the materialized draft (form id, automation graph id, checklist canvas node id, etc.) once written */
  targetId?: string | null
  status?: BoundOutputStatus
  lastMaterializedAt?: Timestamp | null
}

export const BOUND_INTENTS: BoundIntent[] = [
  'DASHBOARD',
  'CHECKLIST_BLUEPRINT',
  'FORM_LAYOUT',
  'AUTOMATION_MAP',
  'MIXED',
]

/** Roles that make sense as adapter input for a given intent. Empty list = allow all roles. */
export const BOUND_INTENT_ROLES: Record<BoundIntent, BoundSemanticRole[]> = {
  DASHBOARD: ['CARD', 'KPI'],
  CHECKLIST_BLUEPRINT: ['CHECKLIST_NODE', 'GATE_AND', 'GATE_OR'],
  FORM_LAYOUT: ['FORM_FIELD', 'FORM_SECTION'],
  AUTOMATION_MAP: ['AUTOMATION_TRIGGER', 'AUTOMATION_ACTION', 'AUTOMATION_BRANCH'],
  MIXED: [],
}

export type DesignAsset = {
  id: string
  workspaceId: string
  title: string
  mode: DesignMode
  canvas?: DesignCanvasSettings | null
  shapes: DesignShape[]
  templateOf?: string | null
  createdById: string
  createdByName: string
  createdAt: Timestamp | null
  updatedAt: Timestamp | null
  /** legacy: pre-rewrite blocks list, kept for migration */
  blocks?: unknown
  /** Phase A additive Bound metadata — see agents/bound-design-plan.md */
  boundIntent?: BoundIntent
  boundVersion?: number
  boundOutput?: BoundOutput
  boundOutputStatus?: BoundOutputStatus
}

export type DesignAssetDraft = {
  id: string
  workspaceId: string
  title: string
  mode: DesignMode
  canvas?: DesignCanvasSettings | null
  shapes: DesignShape[]
  templateOf?: string | null
  boundIntent?: BoundIntent
  boundVersion?: number
  boundOutput?: BoundOutput
  boundOutputStatus?: BoundOutputStatus
}

export type DesignCanvasSettings = {
  width: number
  height: number
  grid: number
  presetId?: string | null
}

export const DEFAULT_DESIGN_CANVAS: DesignCanvasSettings = {
  width: 1600,
  height: 960,
  grid: 24,
  presetId: 'graphite-grid',
}

export type DesignEmbedShape = DesignShape & {
  resolved?: { title?: string; color?: string | null; progress?: number } | null
}

export type DesignEmbedSnapshot = {
  assetId: string
  workspaceId: string
  title: string
  mode: DesignMode
  canvas?: DesignCanvasSettings | null
  bounds: { x: number; y: number; width: number; height: number }
  shapes: DesignEmbedShape[]
  serializedAt: number
}

export const DESIGN_SHAPE_TYPES: DesignShapeType[] = [
  'RECT',
  'ROUNDED_RECT',
  'ELLIPSE',
  'TRIANGLE',
  'POLYGON',
  'STAR',
  'LINE',
  'ARROW',
  'POLYLINE',
  'CURVE',
  'DIAMOND',
  'PARALLELOGRAM',
  'HEXAGON',
  'CYLINDER',
  'DOCUMENT',
  'TERMINATOR',
  'OFF_PAGE',
  'PROCESS',
  'BUBBLE',
  'TEXT',
  'STICKY',
  'FREEHAND',
  'IMAGE',
  'ICON',
  'CHART',
  'CONNECTOR',
  'GROUP',
]

export const isDesignShapeType = (value: unknown): value is DesignShapeType =>
  typeof value === 'string' && (DESIGN_SHAPE_TYPES as string[]).includes(value)

const solid = (color: DesignColor): DesignFill => ({ kind: 'SOLID', color })

const DEFAULT_FILL_FOR_TYPE: Partial<Record<DesignShapeType, DesignFill>> = {
  RECT: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  ROUNDED_RECT: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  PROCESS: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  ELLIPSE: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  BUBBLE: solid('#0f172a'), // mosaic-keep: data (default design shape fill color)
  TRIANGLE: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  DIAMOND: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  PARALLELOGRAM: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  HEXAGON: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  CYLINDER: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  DOCUMENT: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  TERMINATOR: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  OFF_PAGE: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  POLYGON: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  STAR: solid('#1e293b'), // mosaic-keep: data (default design shape fill color)
  STICKY: solid('#fde68a'), // mosaic-keep: data (default design shape fill color)
  TEXT: { kind: 'NONE' },
  IMAGE: { kind: 'NONE' },
  ICON: { kind: 'NONE' },
  CHART: { kind: 'NONE' },
  CONNECTOR: { kind: 'NONE' },
  LINE: { kind: 'NONE' },
  ARROW: { kind: 'NONE' },
  POLYLINE: { kind: 'NONE' },
  CURVE: { kind: 'NONE' },
  FREEHAND: { kind: 'NONE' },
  GROUP: { kind: 'NONE' },
}

const DEFAULT_STROKE_FOR_TYPE: Partial<Record<DesignShapeType, DesignStroke | null>> = {
  RECT: { color: '#38bdf8', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  ROUNDED_RECT: { color: '#38bdf8', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  PROCESS: { color: '#a855f7', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  ELLIPSE: { color: '#38bdf8', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  BUBBLE: { color: '#38bdf8', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  TRIANGLE: { color: '#38bdf8', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  DIAMOND: { color: '#f59e0b', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  PARALLELOGRAM: { color: '#22d3ee', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  HEXAGON: { color: '#fb923c', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  CYLINDER: { color: '#34d399', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  DOCUMENT: { color: '#94a3b8', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  TERMINATOR: { color: '#f472b6', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  OFF_PAGE: { color: '#a3e635', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  POLYGON: { color: '#38bdf8', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  STAR: { color: '#fcd34d', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  LINE: { color: '#cbd5e1', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  ARROW: { color: '#cbd5e1', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  POLYLINE: { color: '#cbd5e1', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  CURVE: { color: '#cbd5e1', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  CONNECTOR: { color: '#94a3b8', width: 2, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  FREEHAND: { color: '#f8fafc', width: 3, style: 'SOLID' }, // mosaic-keep: data (default design shape stroke color)
  STICKY: null,
  TEXT: null,
  IMAGE: null,
  ICON: null,
  CHART: null,
  GROUP: null,
}

const baseShape = (
  id: string,
  type: DesignShapeType,
  position: { x: number; y: number },
  size: { width: number; height: number },
): DesignShapeBase => ({
  id,
  position,
  size,
  rotation: 0,
  opacity: 1,
  fill: DEFAULT_FILL_FOR_TYPE[type] ?? { kind: 'NONE' },
  stroke: DEFAULT_STROKE_FOR_TYPE[type] ?? null,
  effects: [],
  blendMode: 'NORMAL',
})

export const createShape = (
  type: DesignShapeType,
  position: { x: number; y: number },
  size: { width: number; height: number },
  id: string,
): DesignShape => {
  const b = baseShape(id, type, position, size)
  switch (type) {
    case 'RECT':
      return { ...b, type, cornerRadius: 0 }
    case 'ROUNDED_RECT':
    case 'PROCESS':
      return { ...b, type, cornerRadius: 16 }
    case 'ELLIPSE':
    case 'BUBBLE':
      return { ...b, type }
    case 'TRIANGLE':
    case 'DIAMOND':
    case 'PARALLELOGRAM':
    case 'HEXAGON':
    case 'CYLINDER':
    case 'DOCUMENT':
    case 'TERMINATOR':
    case 'OFF_PAGE':
      return { ...b, type }
    case 'POLYGON':
      return { ...b, type, sides: 6 }
    case 'STAR':
      return { ...b, type, sides: 5, innerRatio: 0.5 }
    case 'LINE':
      return {
        ...b,
        type,
        arrowStart: 'NONE',
        arrowEnd: 'NONE',
        path: {
          variant: 'STRAIGHT',
          p1: { x: position.x, y: position.y + size.height / 2 },
          p2: { x: position.x + size.width, y: position.y + size.height / 2 },
        },
      }
    case 'ARROW':
      return {
        ...b,
        type,
        arrowStart: 'NONE',
        arrowEnd: 'TRIANGLE',
        path: {
          variant: 'STRAIGHT',
          p1: { x: position.x, y: position.y + size.height / 2 },
          p2: { x: position.x + size.width, y: position.y + size.height / 2 },
        },
      }
    case 'POLYLINE':
      return {
        ...b,
        type,
        arrowStart: 'NONE',
        arrowEnd: 'NONE',
        points: [
          { x: position.x, y: position.y + size.height },
          { x: position.x + size.width, y: position.y },
        ],
      }
    case 'CURVE':
      return {
        ...b,
        type,
        arrowStart: 'NONE',
        arrowEnd: 'NONE',
        p1: { x: position.x, y: position.y + size.height },
        c1: { x: position.x + size.width / 3, y: position.y },
        c2: { x: position.x + (size.width * 2) / 3, y: position.y + size.height },
        p2: { x: position.x + size.width, y: position.y },
      }
    case 'TEXT':
      return { ...b, type, text: 'Text', textStyle: { ...DEFAULT_TEXT_STYLE } }
    case 'STICKY':
      return {
        ...b,
        type,
        text: '',
        textStyle: { ...DEFAULT_TEXT_STYLE, color: '#0f172a', align: 'LEFT', fontWeight: 500 }, // mosaic-keep: data (default sticky text color)
      }
    case 'FREEHAND':
      return { ...b, type, points: [], smoothing: 0.5 }
    case 'IMAGE':
      return { ...b, type, src: '' }
    case 'ICON':
      return { ...b, type, iconName: 'Star', iconColor: '#f8fafc' } // mosaic-keep: data (default icon color)
    case 'CHART':
      return { ...b, type, chartId: '' }
    case 'CONNECTOR':
      return {
        ...b,
        type,
        from: { kind: 'FREE', x: position.x, y: position.y },
        to: { kind: 'FREE', x: position.x + size.width, y: position.y + size.height },
        routing: 'ORTHOGONAL',
        arrowStart: 'NONE',
        arrowEnd: 'TRIANGLE',
      }
    case 'GROUP':
      return { ...b, type, childIds: [] }
  }
}

export const DEFAULT_SHAPE_SIZE: Record<DesignShapeType, { width: number; height: number }> = {
  RECT: { width: 180, height: 100 },
  ROUNDED_RECT: { width: 180, height: 100 },
  ELLIPSE: { width: 160, height: 110 },
  TRIANGLE: { width: 160, height: 140 },
  POLYGON: { width: 140, height: 140 },
  STAR: { width: 140, height: 140 },
  LINE: { width: 140, height: 40 },
  ARROW: { width: 140, height: 40 },
  POLYLINE: { width: 200, height: 100 },
  CURVE: { width: 200, height: 100 },
  DIAMOND: { width: 160, height: 120 },
  PARALLELOGRAM: { width: 200, height: 110 },
  HEXAGON: { width: 180, height: 110 },
  CYLINDER: { width: 160, height: 130 },
  DOCUMENT: { width: 200, height: 130 },
  TERMINATOR: { width: 180, height: 80 },
  OFF_PAGE: { width: 170, height: 130 },
  PROCESS: { width: 180, height: 100 },
  BUBBLE: { width: 160, height: 100 },
  TEXT: { width: 200, height: 40 },
  STICKY: { width: 180, height: 180 },
  FREEHAND: { width: 0, height: 0 },
  IMAGE: { width: 240, height: 160 },
  ICON: { width: 48, height: 48 },
  CHART: { width: 320, height: 220 },
  CONNECTOR: { width: 200, height: 100 },
  GROUP: { width: 0, height: 0 },
}

export const validateDesignAssetDraft = (draft: DesignAssetDraft): string[] => {
  const errors: string[] = []
  if (!draft.id) errors.push('Design asset id is required.')
  if (!draft.workspaceId) errors.push('Workspace id is required.')
  if (!draft.title.trim()) errors.push('Title cannot be empty.')
  if (!Array.isArray(draft.shapes)) errors.push('Shapes must be an array.')
  else {
    draft.shapes.forEach((shape, i) => {
      if (!shape.id) errors.push(`Shape ${i} missing id.`)
      if (!isDesignShapeType(shape.type)) errors.push(`Shape ${i} invalid type.`)
    })
  }
  if (draft.mode !== 'BOUND' && draft.mode !== 'FREE') errors.push('Mode must be BOUND or FREE.')
  return errors
}

export const isBoundableShape = (shape: DesignShape): boolean =>
  BOUNDABLE_SHAPE_TYPES.includes(shape.type)

const bboxFromPoints = (points: Array<{ x: number; y: number }>) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const p of points) {
    if (p.x < minX) minX = p.x
    if (p.y < minY) minY = p.y
    if (p.x > maxX) maxX = p.x
    if (p.y > maxY) maxY = p.y
  }
  return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) }
}

export const shapeBoundingBox = (shape: DesignShape) => {
  if (shape.type === 'FREEHAND' && shape.points.length > 0) return bboxFromPoints(shape.points)
  if (shape.type === 'POLYLINE' && shape.points.length > 0) return bboxFromPoints(shape.points)
  if (shape.type === 'CURVE') return bboxFromPoints([shape.p1, shape.c1, shape.c2, shape.p2])
  if ((shape.type === 'LINE' || shape.type === 'ARROW') && shape.path) return bboxFromPoints([shape.path.p1, shape.path.p2])
  return { x: shape.position.x, y: shape.position.y, width: shape.size.width, height: shape.size.height }
}

export const designBoundsForShapes = (shapes: DesignShape[]) => {
  if (shapes.length === 0) return { x: 0, y: 0, width: 800, height: 600 }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const s of shapes) {
    const b = shapeBoundingBox(s)
    if (b.x < minX) minX = b.x
    if (b.y < minY) minY = b.y
    if (b.x + b.width > maxX) maxX = b.x + b.width
    if (b.y + b.height > maxY) maxY = b.y + b.height
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 800, height: 600 }
  return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) }
}

/* ---------- Legacy compatibility (old block model) ---------- */

export type LegacyDesignBlockType = 'BUBBLE' | 'DIAMOND' | 'PROCESS' | 'CONNECTOR' | 'LABEL'

export type LegacyDesignBlock = {
  id: string
  type: LegacyDesignBlockType
  props: {
    label?: string
    color?: string | null
    linkedNodeId?: string | null
    logicTrigger?: DesignLogicTrigger | null
    fromBlockId?: string | null
    toBlockId?: string | null
    width?: number
    height?: number
  }
  position: { x: number; y: number }
  dataMapping?: { linkedNodeId: string | null }
}

export type DesignBlock = DesignShape

export const migrateLegacyBlocks = (raw: unknown): DesignShape[] => {
  if (!Array.isArray(raw)) return []
  const newId = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
  const shapes: DesignShape[] = []
  const idMap = new Map<string, string>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const legacy = item as LegacyDesignBlock
    const newSid = legacy.id ? legacy.id : newId()
    idMap.set(legacy.id, newSid)
    if (legacy.type === 'CONNECTOR') continue
    const type: DesignShapeType = legacy.type === 'BUBBLE'
      ? 'BUBBLE'
      : legacy.type === 'DIAMOND'
      ? 'DIAMOND'
      : legacy.type === 'PROCESS'
      ? 'PROCESS'
      : 'TEXT'
    const size = {
      width: legacy.props?.width ?? DEFAULT_SHAPE_SIZE[type].width,
      height: legacy.props?.height ?? DEFAULT_SHAPE_SIZE[type].height,
    }
    const shape = createShape(type, legacy.position, size, newSid)
    if (legacy.props?.color) shape.stroke = { color: legacy.props.color, width: 2, style: 'SOLID' }
    if (legacy.props?.label) shape.text = legacy.props.label
    if (legacy.props?.linkedNodeId) shape.binding = { linkedNodeId: legacy.props.linkedNodeId }
    if (legacy.props?.logicTrigger) shape.logicTrigger = legacy.props.logicTrigger
    shapes.push(shape)
  }
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const legacy = item as LegacyDesignBlock
    if (legacy.type !== 'CONNECTOR') continue
    const fromSid = legacy.props?.fromBlockId ? idMap.get(legacy.props.fromBlockId) : null
    const toSid = legacy.props?.toBlockId ? idMap.get(legacy.props.toBlockId) : null
    const cId = legacy.id ? legacy.id : newId()
    const connector = createShape('CONNECTOR', legacy.position, { width: 200, height: 100 }, cId) as DesignConnectorShape
    if (fromSid) connector.from = { kind: 'SHAPE', shapeId: fromSid }
    if (toSid) connector.to = { kind: 'SHAPE', shapeId: toSid }
    shapes.push(connector)
  }
  return shapes
}

/* ---------- Constants used by old codepaths (kept as fallbacks) ---------- */

export const DESIGN_CANVAS_WIDTH = 4000
export const DESIGN_CANVAS_HEIGHT = 3000
